import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { useUserDataContext } from '../../../contexts/userDataProvider';
import { defaultRangeKey } from '../../common/appSettings';
import { initOrderData } from '../../common/utils';
import { useEdit, useRemove } from '../../common/requests.hooks';
import ordersAPI from '../../../services/ordersAPI';
import Loader from '../../common/Loader';
import ErrorAlert from '../../common/ErrorAlert';
import OrderForm from './OrderForm';
import ModalComponent from '../../common/ModalComponent';

const initDataRange = {
  startDate: new Date(),
  endDate: null,
  key: defaultRangeKey
};

const EditOrder = ({
  clickedItemData = {
    orderId: undefined,
    propertyName: undefined,
    costPerNight: NaN,
    dateFrom: undefined,
    dateTo: undefined,
    description: undefined,
    email: undefined,
    feeding: false,
    firstName: undefined,
    phone: undefined,
    propertyId: undefined,
    status: undefined,
    surname: undefined,
    totalCost: NaN
  },
  closeEditModalReloadData,
  allOrders,
  timeLineLoadedDataRange
}) => {
  const { translate } = useUserDataContext();

  const [dataRange, setDataRange] = useState(initDataRange);
  const [orderData, setOrderData] = useState(initOrderData);
  const [isRemoveOrderModal, setIsRemoveOrderModal] = useState(false);

  const removeOrderOnSuccess = () => {
    setIsRemoveOrderModal(false);
    closeEditModalReloadData();
  };

  const { isLoading: editing, error: editError, sendEditDataRequest } = useEdit(
    (payload) => ordersAPI.editOrder(payload.orderId, payload.data),
    {
      onSuccess: () => {
        setOrderData(initOrderData);
        closeEditModalReloadData();
      }
    }
  );

  const {
    isLoading: isRemoving,
    error: removeError,
    sendRemoveRequest,
  } = useRemove(
    () => ordersAPI.removeOrder(orderData.id),
    {
      callback: removeOrderOnSuccess
    }
  );

  // Filter data to send only edited fields to backend
  const getOnlyEditedFields = (initData, editedData, editedDataRange) => {
    const diff = {};

    // Compare normal edited fields
    if (editedData) {
      Object.keys(editedData).forEach((key) => {
        if (initData[key] !== editedData[key]) {
          diff[key] = editedData[key];
        }
      });
    }

    // Compare date range (startDate / endDate)
    if (editedDataRange) {
      const startISO = editedDataRange.startDate?.toISOString();
      const endISO = editedDataRange.endDate?.toISOString();

      if (startISO && initData.dateFrom !== startISO) {
        diff.dateFrom = startISO;
      }

      if (endISO && initData.dateTo !== endISO) {
        diff.dateTo = endISO;
      }
    }

    return diff;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const editedOrderData = getOnlyEditedFields(clickedItemData, orderData, dataRange);

    sendEditDataRequest({
      orderId: orderData.orderId,
      data: editedOrderData
    });
  };

  const handleFormData = (orderData) => {
    const key = Object.keys(orderData)[0];

    setOrderData(prevState => ({
      ...prevState,
      [key]: orderData[key]
    }));
  };

  // Init by loaded order data
  useEffect(() => {
      setDataRange({
        startDate: new Date(clickedItemData.dateFrom),
        endDate: new Date(clickedItemData.dateTo),
        key: defaultRangeKey // TODO: can be used for different range color in DateRangePicker
      });
      setOrderData(clickedItemData);
  }, [clickedItemData]);

  return (
    <div className="order-view-wrap">
      <Loader 
        setIsLoading={editing || isRemoving}
      />
      <ErrorAlert
        error={editError || removeError}
      />
      <ModalComponent 
        ModalContent={translate('thisReservationWillBeRemoved')}
        onSubmitButtonHandler={sendRemoveRequest}
        onSubmitButtonName={translate('yesRemove')}
        show={isRemoveOrderModal}
        handleClose={() => setIsRemoveOrderModal(false)}
        type="WARNING"
      />
      <OrderForm
        translate={translate}
        allOrders={allOrders}
        timeLineLoadedDataRange={timeLineLoadedDataRange}
        dataRange={dataRange}
        orderData={orderData}
        isOrderEdit={true}
        handleFormData={handleFormData}
        handleSubmit={handleSubmit}
        setDataRange={setDataRange}
        setOrderData={setOrderData} // On Property click - to get reserved data for DateRangePicker
        setIsRemoveOrderModal={setIsRemoveOrderModal}
      />
    </div>
  );
};

EditOrder.propTypes = {
  clickedItemData: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    propertyName: PropTypes.string.isRequired,
    costPerNight: PropTypes.number.isRequired,
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    feeding: PropTypes.bool.isRequired,
    firstName: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    propertyId: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    surname: PropTypes.string.isRequired,
    totalCost: PropTypes.number
  }),
  closeEditModalReloadData: PropTypes.func.isRequired,
  allOrders: PropTypes.arrayOf(
    PropTypes.shape({})
  ),
  timeLineLoadedDataRange: PropTypes.shape({
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date)
  }).isRequired
};

export default EditOrder;
